import React, { useState } from 'react';
import { aiAPI } from '../../services/api';

function MusicPromptGenerator() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);   // { prompt, direction, tracksAnalyzed }
  const [history, setHistory] = useState([]);   // last 10 generated prompts
  const [copied, setCopied] = useState(false);

  const generate = async () => {
    setLoading(true);
    setError('');
    setCopied(false);

    try {
      const response = await aiAPI.generateMusicPrompt();
      const data = response.data;

      if (!data.success || !data.prompt) {
        throw new Error(data.error || 'AI kon geen prompt genereren');
      }

      const entry = {
        id: Date.now(),
        prompt: data.prompt,
        direction: data.direction,
        tracksAnalyzed: data.tracksAnalyzed,
        generatedAt: new Date()
      };

      setResult(entry);
      setHistory(prev => [entry, ...prev].slice(0, 10));
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Onbekende fout');
    } finally {
      setLoading(false);
    }
  };

  const copyPrompt = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const formatTime = (date) =>
    new Date(date).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit', second: '2-digit' });

  return (
    <div className="admin-section">
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h2 style={{ margin: 0 }}>🎶 Muziek Prompt Generator</h2>
          <p style={{ margin: '6px 0 0', color: '#666', fontSize: '14px' }}>
            Genereert een unieke muziek AI prompt op basis van alle nummers in de bibliotheek.
            Elke generatie is anders — zelfde vibes, nooit hetzelfde nummer.
          </p>
        </div>
      </div>

      {/* Generate button */}
      <div style={{ marginBottom: '28px', textAlign: 'center' }}>
        <button
          onClick={generate}
          disabled={loading}
          style={{
            padding: '14px 40px',
            fontSize: '16px',
            fontWeight: '700',
            background: loading
              ? '#aaa'
              : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '12px',
            cursor: loading ? 'not-allowed' : 'pointer',
            boxShadow: loading ? 'none' : '0 4px 15px rgba(102, 126, 234, 0.4)',
            transition: 'all 0.2s',
            letterSpacing: '0.5px'
          }}
        >
          {loading ? '⏳ AI analyseert bibliotheek...' : '✨ Genereer Muziek Prompt'}
        </button>
        <p style={{ marginTop: '10px', fontSize: '13px', color: '#888' }}>
          Gebruikt alle nummertitels als basis — elke klik geeft een unieke prompt
        </p>
      </div>

      {/* Error */}
      {error && (
        <div style={{
          background: '#fff5f5',
          border: '1px solid #feb2b2',
          borderRadius: '8px',
          padding: '14px 18px',
          color: '#c53030',
          marginBottom: '20px',
          fontSize: '14px'
        }}>
          ❌ {error}
        </div>
      )}

      {/* Latest result */}
      {result && (
        <div style={{
          background: 'linear-gradient(135deg, #f0fff4 0%, #ebf8ff 100%)',
          border: '2px solid #68d391',
          borderRadius: '14px',
          padding: '24px',
          marginBottom: '28px'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '14px' }}>
            <div>
              <span style={{
                background: '#48bb78',
                color: 'white',
                padding: '3px 10px',
                borderRadius: '20px',
                fontSize: '12px',
                fontWeight: '700',
                marginRight: '10px'
              }}>
                ✅ NIEUWSTE PROMPT
              </span>
              <span style={{
                background: '#667eea',
                color: 'white',
                padding: '3px 10px',
                borderRadius: '20px',
                fontSize: '12px',
                fontWeight: '600'
              }}>
                🎭 {result.direction}
              </span>
            </div>
            <span style={{ fontSize: '12px', color: '#888' }}>
              {formatTime(result.generatedAt)} · {result.tracksAnalyzed} nummers geanalyseerd
            </span>
          </div>

          <p style={{
            fontSize: '17px',
            lineHeight: '1.6',
            color: '#1a202c',
            margin: '0 0 16px',
            fontStyle: 'italic',
            background: 'white',
            padding: '16px 20px',
            borderRadius: '10px',
            border: '1px solid #c6f6d5',
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
          }}>
            "{result.prompt}"
          </p>

          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={() => copyPrompt(result.prompt)}
              style={{
                padding: '8px 20px',
                background: copied ? '#48bb78' : '#667eea',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                transition: 'background 0.2s'
              }}
            >
              {copied ? '✅ Gekopieerd!' : '📋 Kopieer Prompt'}
            </button>
            <button
              onClick={generate}
              disabled={loading}
              style={{
                padding: '8px 20px',
                background: 'white',
                color: '#667eea',
                border: '2px solid #667eea',
                borderRadius: '8px',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '600'
              }}
            >
              🔄 Nog een genereren
            </button>
          </div>
        </div>
      )}

      {/* Info box */}
      <div style={{
        background: '#fffbeb',
        border: '1px solid #f6e05e',
        borderRadius: '10px',
        padding: '16px 20px',
        marginBottom: '28px',
        fontSize: '14px',
        color: '#744210'
      }}>
        <strong>💡 Hoe te gebruiken:</strong> Kopieer de prompt en plak hem in een muziek AI tool zoals{' '}
        <strong>Suno.ai</strong>, <strong>Udio.com</strong> of <strong>Stable Audio</strong>.
        De AI analyseert de sfeer van jouw muziekbibliotheek en genereert elke keer een andere creatieve prompt
        die past binnen dezelfde vibes maar nooit een kopie is.
      </div>

      {/* History */}
      {history.length > 1 && (
        <div>
          <h3 style={{ marginBottom: '14px', color: '#4a5568' }}>
            🕐 Eerdere Prompts ({history.length - 1})
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {history.slice(1).map(item => (
              <div
                key={item.id}
                style={{
                  background: 'white',
                  border: '1px solid #e2e8f0',
                  borderRadius: '10px',
                  padding: '14px 18px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  gap: '14px'
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '6px', alignItems: 'center' }}>
                    <span style={{
                      background: '#edf2f7',
                      color: '#4a5568',
                      padding: '2px 8px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: '600'
                    }}>
                      🎭 {item.direction}
                    </span>
                    <span style={{ fontSize: '12px', color: '#a0aec0' }}>{formatTime(item.generatedAt)}</span>
                  </div>
                  <p style={{ margin: 0, fontSize: '14px', color: '#2d3748', fontStyle: 'italic' }}>
                    "{item.prompt}"
                  </p>
                </div>
                <button
                  onClick={() => copyPrompt(item.prompt)}
                  title="Kopieer prompt"
                  style={{
                    padding: '6px 12px',
                    background: '#edf2f7',
                    color: '#4a5568',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    flexShrink: 0
                  }}
                >
                  📋
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default MusicPromptGenerator;
